import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Sticky, StickyContainer } from "react-sticky";
import PropTypes from "prop-types";
import AtlasMap from "./AtlasMap";
import AtlasStops from "./AtlasStops";
import Loader from "../loader/Loader";
import AtlasMetadata from "./AtlasMetadata";
import { useBreakpointsContext, useScroll } from "../../hooks";
import {
    ITINERARY_HEADER_HEIGHT,
    ATLAS_MAP_HEIGHT_MOBILE,
    ITINERARY_HEADER_HEIGHT_MOBILE,
    MAP_HEADER_HEIGHT_MOBILE,
} from "../../constants";
import { useTranslate } from "../../translate";
import { getDefaultIsAutoFollowActiveAtlas } from "../../store";
import { useWindowHeight } from "../planner/Planner";
import AtlasMenu from "./AtlasMenu";
import { createFitBounds } from "../route-explorer/MapContainer";
import { usePagedAtlases } from "./hooks";
import { transformLineToPolygon } from "./utils";
import { Helmet } from "react-helmet";
import { trackStopInView } from "../shared/AnalyticsTracker";

function getAtlasCardsTopOffset({ windowHeight, useTwoColumnLayout }) {
    let stickyContentHeight;
    if (!useTwoColumnLayout) {
        stickyContentHeight = ITINERARY_HEADER_HEIGHT_MOBILE + ATLAS_MAP_HEIGHT_MOBILE + MAP_HEADER_HEIGHT_MOBILE;
    } else {
        stickyContentHeight = ITINERARY_HEADER_HEIGHT;
    }
    return stickyContentHeight + (windowHeight - stickyContentHeight) / 4;
}

function Atlas(props) {
    const { atlasId } = props;
    const translate = useTranslate();
    const windowHeight = useWindowHeight();
    const { isExtraSmall } = useBreakpointsContext();
    const useTwoColumnLayout = !isExtraSmall;

    //Filters
    const [filters, setFilters] = useState([]);

    //Pagination
    const itemsPerPage = 30;
    const [currentPage, setCurrentPage] = useState(1);
    const { data, totalCount, loading, error } = usePagedAtlases(
        atlasId,
        currentPage * itemsPerPage - itemsPerPage,
        itemsPerPage,
        filters.map(filter => filter.id),
    );
    const items = data?.items ?? [];
    const markers = useMemo(() => {
        return items.map((item, index) => ({
            longitude: item.coords.lon,
            latitude: item.coords.lat,
            id: item.id,
            label: item.order,
        }));
    }, [items]);

    //Autofollow
    const [isMetadataInScrollView, setIsMetadataInScrollView] = useState();
    const [isAutoFollowEnabled, setIsAutoFollowEnabled] = useState(getDefaultIsAutoFollowActiveAtlas());
    const cardsOffsetTop = getAtlasCardsTopOffset({ windowHeight, useTwoColumnLayout });
    const [activeStopId, setActiveStopId] = useState(null);
    const [stopsPaddingBottom, setStopsPaddingBottom] = useState(null);
    const lastStopId = useMemo(() => {
        const stops = data?.items;
        if (!stops || !stops.length) {
            return;
        }
        return stops[stops.length - 1].id;
    }, [data]);
    const [viewport, setViewport] = useState({
        latitude: 45.958913,
        longitude: -78.328043,
        zoom: 5,
    });

    //Favorites
    const [favoriteIds, setFavoriteIds] = useState([]);
    const handleFavoriteClick = stopId => {
        if (favoriteIds.includes(stopId)) {
            setFavoriteIds(favoriteIds.filter(item => item !== stopId));
        } else {
            setFavoriteIds([...favoriteIds, stopId]);
        }
    };

    //HACK: fix geoJSONObject
    const geojsonObject = useMemo(() => {
        const geojsonObject = data?.placeData?.placeOutline;
        return transformLineToPolygon(geojsonObject);
    }, [data]);
    const stopsById = useMemo(() => {
        const stops = data?.items;
        if (!stops) {
            return {};
        }
        return stops.reduce((result, item) => {
            return {
                ...result,
                [item.id]: item,
            };
        }, {});
    }, [data]);

    const handleMapBtnClick = () => {};
    const onMarkerClick = useCallback(
        id => {
            let query = `.js__stop-card[data-stop-id="${id}"]`;
            const cardEl = document.querySelector(query);
            if (!cardEl) {
                return;
            }
            const cardElY = cardEl.getBoundingClientRect().y;
            window.scrollBy({
                left: 0,
                behavior: "smooth",
                top: cardElY - cardsOffsetTop + 5,
            });
        },
        [cardsOffsetTop],
    );

    //Disable autofollow on mobile
    useEffect(() => {
        if (!useTwoColumnLayout) {
            setIsAutoFollowEnabled(false);
        } else {
            setIsAutoFollowEnabled(getDefaultIsAutoFollowActiveAtlas());
        }
    }, [useTwoColumnLayout]);

    //Autofollow
    useEffect(() => {
        if (!activeStopId || !isAutoFollowEnabled) {
            return;
        }
        setViewport({
            latitude: stopsById[activeStopId].coords.lat,
            longitude: stopsById[activeStopId].coords.lon,
            zoom: 14,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStopId]);
    useScroll(
        useCallback(() => {
            const atlasMetadataEl = document.querySelector(".js__atlas-metadata");
            if (atlasMetadataEl) {
                const { y: nodeY, height: cardHeight } = atlasMetadataEl.getBoundingClientRect();
                const scrollPosition = window.scrollY;

                if (scrollPosition < 200 || (nodeY < cardsOffsetTop && nodeY > cardsOffsetTop - cardHeight)) {
                    //Metadata card is in scroll view
                    setIsMetadataInScrollView(true);
                } else if (isMetadataInScrollView) {
                    setIsMetadataInScrollView(false);
                }
            }

            document.querySelectorAll(".js__stop-card").forEach(cardEl => {
                const stopId = cardEl.dataset.stopId;
                const { y: nodeY, height: cardHeight, bottom } = cardEl.getBoundingClientRect();

                if (nodeY < cardsOffsetTop && nodeY > cardsOffsetTop - cardHeight) {
                    //Stop card is in scroll view
                    setActiveStopId(stopId);
                } else if (activeStopId === stopId) {
                    setActiveStopId(null);
                }

                //Update stop list padding bottom
                if (stopId === lastStopId) {
                    if (useTwoColumnLayout && nodeY < cardsOffsetTop) {
                        //Scroll position is below stop card top
                        //Last stop card reached
                        //In desktop

                        //Calculate stop list padding bottom
                        const offset = 100; //Prevent infinite scroll jump
                        const paddingBottom =
                            bottom < windowHeight ? offset + windowHeight - (bottom - nodeY) - cardsOffsetTop : 0;
                        setStopsPaddingBottom(paddingBottom < 0 ? 0 : paddingBottom);
                    } else {
                        //By default, padding bottom is managed by CSS
                        setStopsPaddingBottom(null);
                    }
                }
            });
        }, [
            setActiveStopId,
            cardsOffsetTop,
            activeStopId,
            isMetadataInScrollView,
            lastStopId,
            useTwoColumnLayout,
            windowHeight,
        ]),
        100,
    );
    //Reset map state on scrolling top
    useEffect(() => {
        if (!isMetadataInScrollView) {
            return;
        }
        const mapboxEl = document.querySelector(".js__atlas-mapbox-gl");
        const mapSize = mapboxEl.getBoundingClientRect();
        const fitBounds = createFitBounds(geojsonObject);
        fitBounds({ mapSize, onViewportChange: setViewport });
        // eslint-disable-next-line
    }, [isMetadataInScrollView]);

    //For tracking active stop in analytics
    useEffect(() => {
        if (activeStopId) {
            trackStopInView("tripper.app", stopsById[activeStopId].en.name);
        }
    }, [activeStopId]);

    if (!data) {
        return <Loader style={{ height: "100vh" }} />;
    }

    const heroImageUrl = data?.atlasMetaData?.defaultPosterImage?.[0]?.medium2x;
    const metadata = data.atlasMetaData;
    const atlasName = translate(metadata, "name") || "";

    return (
        <StickyContainer>
            <Helmet>
                <title>{atlasName || "Loading..."} | Ontario Trip Explorer</title>
            </Helmet>
            <div className="trpr__atlas">
                {useTwoColumnLayout && <div className="trpr__atlas-shadow" aria-hidden />}
                <div className="trpr__atlas-header">
                    <Sticky>
                        {({ style, isSticky, distanceFromTop }) => (
                            <div style={{ position: "relative", ...style, zIndex: 6 }}>
                                <AtlasMenu
                                    resultsCount={items.length}
                                    stops={data.items}
                                    filters={filters}
                                    setFilters={setFilters}
                                    tags={data.atlasMetaData.tags}
                                    showSearchBtn={true}
                                    isTranslucid={heroImageUrl && (!isSticky || distanceFromTop === 0)}
                                    favoriteIds={favoriteIds}
                                    setCurrentPage={setCurrentPage}
                                    totalCount={totalCount}
                                    currentPage={currentPage}
                                />
                            </div>
                        )}
                    </Sticky>
                    <AtlasMetadata
                        className="js__atlas-metadata"
                        heroImageUrl={heroImageUrl}
                        description={translate(metadata, "description")}
                        name={atlasName}
                        searchResultsLabel={translate(metadata, "searchResultsLabel")}
                    />
                </div>
                <AtlasMap
                    className="trpr__atlas-map"
                    activeStopId={activeStopId}
                    geojsonObject={geojsonObject}
                    viewport={viewport}
                    onViewportChange={setViewport}
                    markers={markers}
                    isAutoFollowEnabled={isAutoFollowEnabled}
                    setIsAutoFollowEnabled={setIsAutoFollowEnabled}
                    onMarkerClick={onMarkerClick}
                    useTwoColumnLayout={useTwoColumnLayout}
                />
                <AtlasStops
                    style={{ paddingBottom: stopsPaddingBottom }}
                    stopsById={stopsById}
                    activeStopId={activeStopId}
                    key="stops"
                    className="trpr__atlas-stops"
                    metadata={data.atlasMetaData}
                    stops={items}
                    handleFavoriteClick={handleFavoriteClick}
                    handleMapBtnClick={handleMapBtnClick}
                    favoriteIds={favoriteIds}
                />
            </div>
        </StickyContainer>
    );
}

Atlas.propTypes = {
    atlasId: PropTypes.string.isRequired,
};

export default Atlas;
